import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { useConfirm } from '../context/ConfirmContext';
import { Plus, Star, Clock, CheckCircle, XCircle, MessageSquare, ChevronRight, Film as FilmIcon } from 'lucide-react';
import API from '../utils/api';

const PAGE_SIZE = 5; // films shown per page on the dashboard list

const pageBtnStyle = (active, disabled) => ({
  minWidth: 30, padding: '5px 10px', borderRadius: 6, fontSize: 10,
  fontWeight: active ? 800 : 600,
  background: active ? 'var(--pink)' : 'transparent',
  color: active ? '#fff' : 'var(--text-muted)',
  border: `1.5px solid ${active ? 'var(--pink)' : 'var(--border)'}`,
  cursor: disabled ? 'not-allowed' : 'pointer',
  opacity: disabled ? 0.4 : 1,
  fontFamily: 'var(--font)', textTransform: 'uppercase', letterSpacing: '0.06em',
  transition: 'all 0.2s',
});

const STAGES = ['Submitted','Film Opened','Under Review','Shortlisted','Decision'];
const STAGE_MAP = { submitted:0, opened:1, under_review:2, shortlisted:3, approved:4 };
const STAGE_ICONS_EL = [
  <Clock size={11} />,
  <ChevronRight size={11} />,
  <Star size={11} />,
  <CheckCircle size={11} />,
  <CheckCircle size={11} />,
];

function timeAgo(date) {
  const diff = Date.now() - new Date(date);
  const m = Math.floor(diff / 60000);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function ScoreBar({ label, value }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
        <span style={{ fontSize: 11, color: 'var(--text-secondary)', fontWeight: 500 }}>{label}</span>
        <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text)' }}>{value}/10</span>
      </div>
      <div style={{ height: 4, background: 'var(--bg-elevated)', borderRadius: 2, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${(value/10)*100}%`, background: 'linear-gradient(90deg,var(--pink),var(--pink-light))', borderRadius: 2, transition: 'width 0.8s ease' }} />
      </div>
    </div>
  );
}

function FilmCard({ sub, onEdit, onDelete }) {
  // Was missing before — the "Fix and Resubmit" button below called
  // navigate() with no such variable in scope, a real pre-existing bug.
  const navigate = useNavigate();
  const stage = STAGE_MAP[sub.review_status] ?? 0;
  const isApproved = sub.status === 'approved';
  const isRejected = sub.status === 'rejected';
  const pct = Math.round((stage / 4) * 100);

  const scores = [
    { label:'Cinematography', val:sub.score_cinematography },
    { label:'Screenplay/Engaging', val:sub.score_screenplay },
    { label:'Acting', val:sub.score_acting },
    { label:'Direction', val:sub.score_direction },
    { label:'Dialogue', val:sub.score_dialogue },
    { label:'Music', val:sub.score_music },
    { label:'Editing', val:sub.score_editing },
    sub.score_other_label && { label:sub.score_other_label, val:sub.score_other },
  ].filter(Boolean).filter(s => s.val !== null && s.val !== undefined);

  return (
    <div className="card fade-up" style={{ padding: 24, marginBottom: 14 }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12, gap: 12 }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
            <h3 style={{ fontWeight: 800, fontSize: 18, color: 'var(--text)', letterSpacing: '-0.01em' }}>{sub.title}</h3>
            {sub.is_resubmission && <span className="badge badge-purple">Resubmission</span>}
          </div>
          <p style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 500 }}>
            {[sub.genre, sub.language, sub.duration].filter(Boolean).join(' · ')}
          </p>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
          <span className={`badge ${isApproved ? 'badge-green' : isRejected ? 'badge-red' : 'badge-amber'}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            {isApproved ? <CheckCircle size={11} /> : isRejected ? <XCircle size={11} /> : <Clock size={11} />}
            {isApproved ? 'Approved' : isRejected ? 'Not Selected' : STAGES[stage]}
          </span>
          {isApproved && sub.score_overall && (
            <span style={{ fontWeight: 900, fontSize: 18, color: 'var(--pink)' }}>★ {parseFloat(sub.score_overall).toFixed(1)}</span>
          )}
        </div>
      </div>

      {sub.synopsis && (
        <p style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6, marginBottom: 16, padding: '10px 14px', background: 'var(--bg-elevated)', borderRadius: 'var(--r-md)', borderLeft: '3px solid var(--pink)', fontWeight: 400 }}>
          {sub.synopsis}
        </p>
      )}

      {/* Progress */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
          <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', letterSpacing: '0.1em', textTransform: 'uppercase' }}>Review Progress</span>
          <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 500 }}>{isRejected ? 'Decision made' : `Stage ${stage + 1} of 5`}</span>
        </div>
        <div style={{ height: 3, background: 'var(--bg-elevated)', borderRadius: 2, overflow: 'hidden', marginBottom: 12 }}>
          <div style={{ height: '100%', width: isRejected ? '100%' : `${pct}%`, background: isRejected ? 'var(--red)' : 'linear-gradient(90deg,var(--pink),var(--pink-light))', borderRadius: 2, transition: 'width 1s ease' }} />
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          {STAGES.map((st, i) => {
            const done = i <= stage;
            const curr = i === stage;
            const fail = isRejected && i === stage;
            return (
              <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, flex: 1 }}>
                <div style={{ width: 26, height: 26, borderRadius: '50%', background: fail ? 'rgba(255,68,68,0.12)' : done ? 'var(--pink-glow)' : 'var(--bg-elevated)', border: `2px solid ${fail ? 'var(--red)' : done ? 'var(--pink)' : 'var(--border)'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: fail ? 'var(--red)' : done ? 'var(--pink)' : 'var(--text-muted)', boxShadow: curr && !isRejected ? '0 0 10px var(--pink-glow-strong)' : 'none', transition: 'all 0.3s' }}>
                  {fail ? <XCircle size={11} /> : STAGE_ICONS_EL[i]}
                </div>
                <span style={{ fontSize: 9, color: done ? 'var(--text)' : 'var(--text-muted)', fontWeight: done ? 700 : 400, textAlign: 'center', lineHeight: 1.3, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{st}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Scores */}
      {isApproved && scores.length > 0 && (
        <div style={{ background: 'var(--bg-elevated)', borderRadius: 'var(--r-md)', padding: 16, marginBottom: 12 }}>
          <div className="section-label" style={{ marginBottom: 12 }}>Film Scores</div>
          {scores.map(s => <ScoreBar key={s.label} label={s.label} value={s.val} />)}
        </div>
      )}

      {/* Feedback note */}
      {sub.score_notes && (
        <div style={{ background: 'rgba(255,45,139,0.05)', border: '1.5px solid var(--pink-glow-strong)', borderRadius: 'var(--r-md)', padding: '12px 14px', marginBottom: 12, display: 'flex', gap: 10 }}>
          <MessageSquare size={14} color="var(--pink)" style={{ flexShrink: 0, marginTop: 1 }} />
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6, fontWeight: 400 }}>{sub.score_notes}</p>
        </div>
      )}

      {/* Correction needed */}
      {sub.review_status === 'needs_correction' && sub.correction_reason && (
        <div style={{ background: 'rgba(255,181,71,0.06)', border: '1.5px solid rgba(255,181,71,0.25)', borderRadius: 'var(--r-md)', padding: '12px 14px', marginBottom: 12 }}>
          <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
            <MessageSquare size={14} color="var(--amber, #ffb547)" style={{ flexShrink: 0, marginTop: 1 }} />
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--amber, #ffb547)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>Correction Needed</div>
              <p style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6, fontWeight: 400 }}>{sub.correction_reason}</p>
            </div>
          </div>
          <button onClick={() => navigate(`/submit?edit=${sub.id}`)} className="btn-primary" style={{ fontSize: 12 }}>
            Fix and Resubmit →
          </button>
        </div>
      )}

      {/* Reviewer feedback (merged, anonymous) */}
      {(sub.reviewer_feedback_strengths || sub.reviewer_feedback_improvements || sub.reviewer_feedback_summary) && (
        <div style={{ background: 'var(--bg-elevated)', borderRadius: 'var(--r-md)', padding: 16, marginBottom: 12 }}>
          <div className="section-label" style={{ marginBottom: 10 }}>Reviewer Feedback</div>
          {sub.reviewer_feedback_strengths && (
            <div style={{ marginBottom: 10 }}>
              <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--green, #00C896)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Strengths</span>
              <p style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6, marginTop: 4, whiteSpace: 'pre-line' }}>{sub.reviewer_feedback_strengths}</p>
            </div>
          )}
          {sub.reviewer_feedback_improvements && (
            <div style={{ marginBottom: 10 }}>
              <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--amber, #FFB547)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Areas to Improve</span>
              <p style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6, marginTop: 4, whiteSpace: 'pre-line' }}>{sub.reviewer_feedback_improvements}</p>
            </div>
          )}
          {sub.reviewer_feedback_summary && (
            <div>
              <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--pink)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Summary</span>
              <p style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6, marginTop: 4, whiteSpace: 'pre-line' }}>{sub.reviewer_feedback_summary}</p>
            </div>
          )}
        </div>
      )}

      {/* Licensing agreement — signing happens on paper, coordinated by the
          Shortflix team; this just shows the filmmaker where it stands.
          Hidden once signed unless there's nothing else to show. */}
      {isApproved && (sub.agreement_status === 'sent' || sub.agreement_status === 'viewed') && (
        <div style={{ background: 'rgba(255,181,71,0.06)', border: '1.5px solid rgba(255,181,71,0.25)', borderRadius: 'var(--r-md)', padding: '14px 16px', marginBottom: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--amber, #ffb547)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>Licensing Agreement — Awaiting Signature</div>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6, fontWeight: 400 }}>
            Our team has shared the licensing agreement with you. Once you and Shortflix have both signed, we'll request your final delivery.
          </p>
        </div>
      )}
      {isApproved && sub.agreement_status === 'declined' && (
        <div style={{ background: 'rgba(255,68,68,0.06)', border: '1.5px solid rgba(255,68,68,0.25)', borderRadius: 'var(--r-md)', padding: '14px 16px', marginBottom: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--red, #ff4444)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>Licensing Agreement — On Hold</div>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6, fontWeight: 400 }}>
            {sub.agreement_decline_reason
              ? `The agreement is on hold: ${sub.agreement_decline_reason}. Our team will be in touch.`
              : "The agreement is on hold — our team will be in touch to sort it out."}
          </p>
        </div>
      )}
      {isApproved && sub.agreement_status === 'signed' && !sub.delivery_status && (
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(0,200,150,0.1)', border: '1.5px solid rgba(0,200,150,0.3)', borderRadius: 20, padding: '6px 14px', marginBottom: 12 }}>
          <CheckCircle size={13} color="var(--green)" />
          <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--green)' }}>Licensing agreement signed</span>
        </div>
      )}

      {/* Delivery status — only ever shown for approved films with a delivery on file */}
      {isApproved && sub.delivery_status === 'requested' && (
        <div style={{ background: 'rgba(0,200,150,0.06)', border: '1.5px solid rgba(0,200,150,0.2)', borderRadius: 'var(--r-md)', padding: '14px 16px', marginBottom: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--green)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>Final Delivery Requested</div>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6, marginBottom: 10, fontWeight: 400 }}>Submit your final delivery materials so we can get your film ready to publish.</p>
          <button onClick={() => navigate(`/deliver/${sub.id}`)} className="btn-primary" style={{ fontSize: 12 }}>Start Delivery →</button>
        </div>
      )}
      {isApproved && sub.delivery_status === 'submitted' && (
        <div style={{ background: 'var(--bg-elevated)', borderRadius: 'var(--r-md)', padding: '12px 14px', marginBottom: 12, fontSize: 13, color: 'var(--text-secondary)', fontWeight: 500 }}>
          Delivery submitted — awaiting QC review
        </div>
      )}
      {isApproved && sub.delivery_status === 'qc_failed' && (
        <div style={{ background: 'rgba(255,181,71,0.06)', border: '1.5px solid rgba(255,181,71,0.25)', borderRadius: 'var(--r-md)', padding: '12px 14px', marginBottom: 12 }}>
          <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
            <MessageSquare size={14} color="var(--amber, #ffb547)" style={{ flexShrink: 0, marginTop: 1 }} />
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--amber, #ffb547)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>Delivery Needs Fixes</div>
              <p style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6, fontWeight: 400 }}>{sub.delivery_qc_notes}</p>
            </div>
          </div>
          <button onClick={() => navigate(`/deliver/${sub.id}`)} className="btn-primary" style={{ fontSize: 12 }}>Fix and Resubmit Delivery →</button>
        </div>
      )}
      {isApproved && sub.delivery_status === 'qc_passed' && sub.release_on_hold && (
        <div style={{ background: 'rgba(255,181,71,0.06)', border: '1.5px solid rgba(255,181,71,0.25)', borderRadius: 'var(--r-md)', padding: '12px 14px', marginBottom: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--amber, #ffb547)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>Release On Hold</div>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6, fontWeight: 400 }}>Your film's release has been put on hold — we'll update you with a new date soon.</p>
        </div>
      )}
      {isApproved && sub.delivery_status === 'qc_passed' && !sub.release_on_hold && sub.scheduled_release_at && (
        <div style={{ background: 'var(--pink-glow)', border: '1.5px solid var(--pink)', borderRadius: 'var(--r-md)', padding: '12px 14px', marginBottom: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--pink)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>Going Live Soon</div>
          <p style={{ fontSize: 13, color: 'var(--text)', lineHeight: 1.6, fontWeight: 600 }}>
            Your film is going live on {new Date(sub.scheduled_release_at).toLocaleString('en-IN', { day: 'numeric', month: 'long', year: 'numeric', hour: 'numeric', minute: '2-digit' })}!
          </p>
        </div>
      )}
      {isApproved && sub.delivery_status === 'qc_passed' && !sub.release_on_hold && !sub.scheduled_release_at && (
        <div style={{ background: 'var(--bg-elevated)', borderRadius: 'var(--r-md)', padding: '12px 14px', marginBottom: 12, fontSize: 13, color: 'var(--text-secondary)', fontWeight: 500 }}>
          QC passed — your film is being moved to the platform
        </div>
      )}
      {isApproved && sub.delivery_status === 'published' && (
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(0,200,150,0.1)', border: '1.5px solid rgba(0,200,150,0.3)', borderRadius: 20, padding: '6px 14px', marginBottom: 12 }}>
          <CheckCircle size={13} color="var(--green)" />
          <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--green)' }}>Published on Shortflix</span>
        </div>
      )}

      {/* Footer */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 12, borderTop: '1.5px solid var(--border)' }}>
        <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 500 }}>Updated {timeAgo(sub.updated_at)}</span>
        <div style={{ display: 'flex', gap: 8 }}>
          {sub.status !== 'approved' && sub.review_status !== 'under_review' && sub.review_status !== 'shortlisted' && (
            <button onClick={() => onDelete(sub)} style={{ background: 'var(--bg-elevated)', color: 'var(--red)', border: '1.5px solid rgba(255,68,68,0.2)', borderRadius: 'var(--r-sm)', padding: '5px 12px', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'var(--font)', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
              Delete
            </button>
          )}
          {sub.review_status === 'submitted' && (
            <button onClick={() => onEdit(sub)} style={{ background: 'var(--bg-elevated)', color: 'var(--blue)', border: '1.5px solid rgba(59,130,246,0.2)', borderRadius: 'var(--r-sm)', padding: '5px 12px', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'var(--font)', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
              Edit
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const confirm = useConfirm();
  const [submissions, setSubmissions] = useState([]);
  const [season, setSeason] = useState(null);
  const [banner, setBanner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [page, setPage] = useState(1);

  // Back to page 1 whenever the filter changes — the old page number rarely
  // makes sense against a different, usually shorter, list.
  useEffect(() => { setPage(1); }, [filter]);

  useEffect(() => {
    Promise.all([
      API.get('/api/submissions/my'),
      API.get('/api/public/active-season'),
      API.get('/api/public/banner'),
    ]).then(([s, se, b]) => {
      setSubmissions(s.data.submissions);
      setSeason(se.data.season);
      setBanner(b.data.banner);
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  const handleDelete = async (sub) => {
    const ok = await confirm(`Delete "${sub.title}"? It'll be removed from your dashboard.`, { title: 'Delete submission?', confirmText: 'Delete' });
    if (!ok) return;
    try {
      await API.put(`/api/submissions/${sub.id}/self-delete`);
      setSubmissions(p => p.filter(s => s.id !== sub.id));
      showToast('Submission deleted');
    } catch (err) {
      showToast(err.response?.data?.error || 'Could not delete submission', 'error');
    }
  };

  const filtered = filter === 'all' ? submissions : submissions.filter(s => s.status === filter);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageStart = (currentPage - 1) * PAGE_SIZE;
  const pageItems = filtered.slice(pageStart, pageStart + PAGE_SIZE);

  // Keep `page` valid if the list shrinks (e.g. after deleting a submission
  // on the last page).
  useEffect(() => { if (page > totalPages) setPage(totalPages); }, [page, totalPages]);

  const goToPage = (n) => {
    setPage(n);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const stats = {
    total: submissions.length,
    review: submissions.filter(s => s.status === 'pending').length,
    approved: submissions.filter(s => s.status === 'approved').length,
    rejected: submissions.filter(s => s.status === 'rejected').length,
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)' }}>
      <Navbar />
      {banner && (
        <div style={{ background: banner.bg_color, color: banner.text_color, padding: '10px 24px', fontSize: 12, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
          {banner.title}
          {banner.subtitle && <span style={{ opacity: 0.8, fontWeight: 400 }}> · {banner.subtitle}</span>}
          {banner.cta_text && <a href={banner.cta_link} style={{ color: banner.text_color, marginLeft: 'auto', fontWeight: 800, textDecoration: 'none' }}>{banner.cta_text} →</a>}
        </div>
      )}

      <div style={{ maxWidth: 860, margin: '0 auto', padding: '40px 24px' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28 }}>
          <div>
            <h1 style={{ fontWeight: 900, fontSize: 30, color: 'var(--text)', marginBottom: 6, letterSpacing: '-0.02em' }}>
              Hey, {user?.name?.split(' ')[0]} 👋
            </h1>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', fontWeight: 500 }}>
              {season
                ? <><span style={{ color: 'var(--green)' }}>●</span> {season.name}{season.end_date && ` · Closes ${new Date(season.end_date).toLocaleDateString('en-IN',{day:'numeric',month:'long'})}`}</>
                : <><span style={{ color: 'var(--green)' }}>●</span> Open submissions · Accepting films year-round</>
              }
            </p>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
            <button onClick={() => navigate('/submit')} className="btn-primary" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Plus size={14} /> Submit Film
            </button>
            <button onClick={() => navigate('/how-to-submit')} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font)', textDecoration: 'underline' }}>
              How to submit? →
            </button>
          </div>
        </div>

        {/* Stats */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 24, flexWrap: 'wrap' }}>
          {[
           { label:'Total', val:stats.total, color:'var(--pink)', f:'all', icon:<FilmIcon size={13} /> },
            { label:'Under Review', val:stats.review, color:'var(--amber)', f:'pending', icon:<Clock size={13} /> },
            { label:'Approved', val:stats.approved, color:'var(--green)', f:'approved', icon:<CheckCircle size={13} /> },
            { label:'Not Selected', val:stats.rejected, color:'var(--red)', f:'rejected', icon:<XCircle size={13} /> },
          ].map(({ label, val, color, f, icon }) => (
            <div key={label} className="card" onClick={() => setFilter(f)}
              style={{ padding: '12px 14px', cursor: 'pointer', borderColor: filter === f ? 'var(--pink)' : 'var(--border)', transition: 'all 0.2s', minWidth: 108, flex: '0 0 auto' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                <div style={{ color: color }}>{icon}</div>
                <div style={{ fontWeight: 900, fontSize: 20, color }}>{val}</div>
              </div>
              <div style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>{label}</div>
            </div>
          ))}
        </div>

        {/* Filter tabs */}
        {submissions.length > 0 && (
          <div style={{ display: 'flex', gap: 6, marginBottom: 18 }}>
            {[['all','All Films'],['pending','Under Review'],['approved','Approved'],['rejected','Not Selected']].map(([val, label]) => (
              <button key={val} onClick={() => setFilter(val)} style={{ padding: '5px 12px', borderRadius: 6, fontSize: 10, fontWeight: filter === val ? 800 : 600, background: filter === val ? 'var(--pink)' : 'transparent', color: filter === val ? '#fff' : 'var(--text-muted)', border: `1.5px solid ${filter === val ? 'var(--pink)' : 'transparent'}`, cursor: 'pointer', transition: 'all 0.2s', fontFamily: 'var(--font)', textTransform: 'uppercase', letterSpacing: '0.08em', boxShadow: filter === val ? '0 2px 8px rgba(255,45,139,0.3)' : 'none' }}>
                {label}
              </button>
            ))}
          </div>
        )}

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 60 }}>
            <div style={{ width: 30, height: 30, border: '3px solid var(--border)', borderTopColor: 'var(--pink)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '80px 0' }}>
            <FilmIcon size={40} color="var(--text-muted)" style={{ margin: '0 auto 16px' }} />
            <h3 style={{ fontWeight: 800, fontSize: 22, marginBottom: 8, color: 'var(--text)' }}>
              {filter === 'all' ? 'No submissions yet' : `No ${filter} films`}
            </h3>
            <p style={{ color: 'var(--text-muted)', marginBottom: 24, fontSize: 14, fontWeight: 400 }}>
              {filter === 'all' ? 'Submit your first film to get started.' : 'Switch filter to see other films.'}
            </p>
            {filter === 'all' && <button onClick={() => navigate('/submit')} className="btn-primary">Submit Your First Film</button>}
          </div>
        ) : (
          <>
            {pageItems.map(sub => <FilmCard key={sub.id} sub={sub} onEdit={s => navigate(`/submit?edit=${s.id}`)} onDelete={handleDelete} />)}

            {filtered.length > PAGE_SIZE && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginTop: 18, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 500 }}>
                  Showing {pageStart + 1}–{Math.min(pageStart + PAGE_SIZE, filtered.length)} of {filtered.length}
                </span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <button onClick={() => goToPage(currentPage - 1)} disabled={currentPage === 1} style={pageBtnStyle(false, currentPage === 1)}>‹ Prev</button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(n => (
                    <button key={n} onClick={() => goToPage(n)} style={pageBtnStyle(n === currentPage, false)}>{n}</button>
                  ))}
                  <button onClick={() => goToPage(currentPage + 1)} disabled={currentPage === totalPages} style={pageBtnStyle(false, currentPage === totalPages)}>Next ›</button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
